import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc } from 'firebase/firestore';
import { Button, Form, Container, Alert } from 'react-bootstrap';

export default function CharacterForm() {
  const { currentUser } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subtitle: '',
    description: '',
    imageUrl: '',
    tags: '',
    requiredUrl: '',
    requiredUrlText: '',
    order: 0,
    style: 'basic',
    position: 'center',
    power: '',
    weakness: '',
    color: '',
    place: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [fetchLoading, setFetchLoading] = useState(true);

  // Fetch existing characters
  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'characters'));
        const chars = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => a.order - b.order);
        setCharacters(chars);
      } catch (error) {
        setMessage('Error loading characters: ' + error.message);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchCharacters();
  }, []);

  // Handle character selection from dropdown
  const handleSelectCharacter = (e) => {
    const charId = e.target.value;
    if (charId === 'new') {
      setSelectedCharacter(null);
      setFormData({
        name: '',
        subtitle: '',
        description: '',
        imageUrl: '',
        tags: '',
        requiredUrl: '',
        requiredUrlText: '',
        order: 0,
        style: 'basic',
        position: 'center',
        power: '',
        weakness: '',
        color: '',
        place: ''
      });
      return;
    }

    const character = characters.find(c => c.id === charId);
    if (character) {
      setSelectedCharacter(character);
      setFormData({
        name: character.name,
        subtitle: character.subtitle || '',
        description: character.description || '',
        imageUrl: character.imageUrl || '',
        tags: character.tags?.join(', ') || '',
        requiredUrl: character.requiredUrl || '',
        requiredUrlText: character.requiredUrlText || '',
        order: character.order || 0,
        style: character.style || 'basic',
        position: character.position || 'center',
        power: character.power || '',
        weakness: character.weakness || '',
        color: character.color || '',
        place: character.place || ''
      });
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const charData = {
      ...formData,
      tags: formData.tags.split(',').map(tag => tag.trim()),
      updatedAt: new Date(),
      updatedBy: currentUser.uid
    };

    if (selectedCharacter) {
      await updateDoc(doc(db, 'characters', selectedCharacter.id), charData);
      setMessage('Character updated successfully!');
    } else {
      await addDoc(collection(db, 'characters'), {
        ...charData,
        createdAt: new Date(),
        createdBy: currentUser.uid
      });
      setMessage('Character added successfully!');
    }

    // Refresh character list
    const querySnapshot = await getDocs(collection(db, 'characters'));
    setCharacters(querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })));

    // Reset form if new character
    if (!selectedCharacter) {
      setFormData({
        name: '',
        subtitle: '',
        description: '',
        imageUrl: '',
        tags: '',
        requiredUrl: '',
        requiredUrlText: '',
        order: 0,
        style: 'basic',
        position: 'center',
        power: '',
        weakness: '',
        color: '',
        place: ''
      });
    }
  } catch (error) {
    setMessage('Error: ' + error.message);
    console.error(error); // <-- Add this line
  } finally {
    setLoading(false);
  }
};

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Container className="mainMargin">
      <h2>{selectedCharacter ? 'Edit Character' : 'Add New Character'}</h2>
      {message && <Alert variant={message.includes('Error') ? 'danger' : 'success'}>{message}</Alert>}
      
      <Form.Group className="mb-4">
        <Form.Label>Select Character to Edit</Form.Label>
        <Form.Select 
          onChange={handleSelectCharacter}
          value={selectedCharacter?.id || 'new'}
          disabled={fetchLoading}
        >
          <option value="new">-- Add New Character --</option>
          {characters.map(character => (
            <option key={character.id} value={character.id}>
              {character.order} {character.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Character Name</Form.Label>
          <Form.Control
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter character name"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Subtitle</Form.Label>
          <Form.Control
            name="subtitle"
            value={formData.subtitle}
            onChange={handleChange}
            placeholder="Character's title or role"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Detailed character description"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Image URL (optional)</Form.Label>
          <Form.Control
            type="url"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Tags (comma-separated)</Form.Label>
          <Form.Control
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="warrior, magic, hero"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Required URL to Unlock (optional)</Form.Label>
          <Form.Control
            type="text"
            name="requiredUrl"
            value={formData.requiredUrl}
            onChange={handleChange}
            placeholder="/url"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Required URL text (optional)</Form.Label>
          <Form.Control
            type="text"
            name="requiredUrlText"
            value={formData.requiredUrlText}
            onChange={handleChange}
            placeholder=""
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Display Order</Form.Label>
          <Form.Control
            type="number"
            name="order"
            value={formData.order}
            onChange={handleChange}
            min="0"
            required
            placeholder="Enter display order number"
          />
          <Form.Text className="text-muted">
            Lower numbers appear first
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Position</Form.Label>
          <Form.Control
            type="text"
            name="position"
            value={formData.position}
            onChange={handleChange}
            required
            placeholder="Enter image position"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Character Style</Form.Label>
          <Form.Select
            name="style"
            value={formData.style}
            onChange={handleChange}
          >
            <option value="basic">Basic</option>
            <option value="special">Special</option>
            <option value="rare">Rare</option>
            <option value="event">Event</option>
          </Form.Select>
          <Form.Text className="text-muted">
            Determines visual presentation
          </Form.Text>
        </Form.Group>

        {/* New fields: power, weakness, color, place */}
        <Form.Group className="mb-3">
          <Form.Label>Power</Form.Label>
          <Form.Control
            type="text"
            name="power"
            value={formData.power}
            onChange={handleChange}
            placeholder="Character's power"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>weakness</Form.Label>
          <Form.Control
            type="text"
            name="weakness"
            value={formData.weakness}
            onChange={handleChange}
            placeholder="Character's weakness"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Color</Form.Label>
          <Form.Control
            type="text"
            name="color"
            value={formData.color}
            onChange={handleChange}
            placeholder="Character's color"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Place</Form.Label>
          <Form.Control
            type="text"
            name="place"
            value={formData.place}
            onChange={handleChange}
            placeholder="Character's place"
          />
        </Form.Group>

        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? (selectedCharacter ? 'Updating...' : 'Saving...') : 
                    (selectedCharacter ? 'Update Character' : 'Save Character')}
        </Button>
        
        {selectedCharacter && (
          <Button
            variant="secondary"
            className="ms-2"
            onClick={() => {
              setSelectedCharacter(null);
              setFormData({
                name: '',
                subtitle: '',
                description: '',
                imageUrl: '',
                tags: '',
                requiredUrl: '',
                requiredUrlText: '',
                order: 0,
                style: 'basic',
                position: 'center',
                power: '',
                weakness: '',
                color: '',
                place: ''
              });
            }}
          >
            Cancel Edit
          </Button>
        )}
      </Form>
    </Container>
  );
}